import type { ChatReadStatus, NewChat } from '../common'
import type { QueryKey, UseInfiniteQueryOptions } from '@tanstack/react-query'

import { useInfiniteQuery } from '@tanstack/react-query'

import http from '../../../lib/http'
import { ChatType } from '../common'

export type ChatsQueryKey = [
  'chats',
  {
    id?: string | string[]
    userId?: string
    patientId?: string
    searchQuery?: string
    status?: ChatReadStatus
    type?: ChatType
  }
]

let PAGE_SIZE = 10

export type ChatPage = NewChat[]

export type UseChatsQueryOptions = ChatsQueryKey[1] & Omit<UseInfiniteQueryOptions, 'queryKey' | 'initialPageParam' | 'getNextPageParam'>

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useChatsQuery ({ id, userId, patientId, searchQuery, status, type = ChatType.Own, ...options }: UseChatsQueryOptions) {
  return useInfiniteQuery({
    queryKey: getChatsQueryKey({ id, userId, patientId, searchQuery, status, type }),
    async queryFn ({ queryKey, pageParam }: {queryKey: QueryKey, pageParam: unknown}): Promise<ChatPage> {
      let [ , { id, userId, patientId, searchQuery, status, type }] = queryKey as ChatsQueryKey

      if (typeof userId !== 'string' || !userId) {
        throw new Error('Invalid userId')
      }

      if (type === ChatType.Current && typeof patientId !== 'string') {
        throw new Error('Invalid patientId for current chat type')
      }

      let ids = typeof id === 'string' ? [ id ] : id ?? []
      let patientIds: string[] = []
      let participantIds: string[] = []
      let types: string[] = []

      switch (type) {
        case ChatType.Own:
          patientIds = []
          participantIds = [ userId ]
          types = [ 'active' ]
          break

        case ChatType.Current:
          patientIds = [ patientId as string ]
          participantIds = [ userId ]
          types = [ 'current' ]
          break

        case ChatType.Concierge:
          patientIds = []
          participantIds = []
          types = [ 'concierge' ]
          break

        case ChatType.Archived:
          patientIds = []
          participantIds = [ userId ]
          types = [ 'archived' ]
          break
      }

      // ToDo: remove page limit hack
      let params = new URLSearchParams([
        ...(typeof PAGE_SIZE === 'number' ? [[ 'limit', status === 'unread' ? '999' : PAGE_SIZE.toString() ]] : []),
        ...(pageParam && typeof pageParam === 'number' ? [[ 'offset', (pageParam * 10).toString(10) ]] : []),
        ...(participantIds.length > 0 ? participantIds.map(id => [ 'filters[participant-id]', id ]) : []),
        ...(patientIds.length > 0 ? patientIds.map(id => [ 'filters[patient-id]', id ]) : []),
        ...(types.length > 0 ? types.map(type => [ 'filters[type]', type ]) : []),
        ...(ids.length > 0 ? ids.map(id => [ 'filters[id]', id ]) : []),
        ...(typeof searchQuery === 'string' && searchQuery.length > 0 ? [[ 'filters[search-query]', searchQuery ]] : []),
        ...(typeof status === 'string' ? [[ 'filters[status]', status ]] : [])
      ])

      const data = await http.getRequest(`/chats?${params.toString()}`, {})
      return data as ChatPage
    },
    initialPageParam: 0,
    getNextPageParam: (_, allPages) => allPages.length + 1,
    getPreviousPageParam: (_, allPages) => allPages.length - 1,
    enabled: Boolean(
      typeof userId === 'string' &&
        ((type === ChatType.Current && typeof patientId === 'string' && patientId.length > 0) || type !== ChatType.Current)
    ),
    refetchOnWindowFocus: false
  })
}

export function getChatsQueryKey (options: UseChatsQueryOptions): ChatsQueryKey {
  return [
    'chats',
    { ...options }
  ]
}
